import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import isNil from 'lodash/isNil'
import moment from 'moment'

import { observer } from 'mobx-react'

import Text from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { actions } from 'modules/automations/store/collections/actions'
import { automations } from 'modules/automations/store/collections/automations'
import { Trigger } from 'modules/automations/store/models/Trigger'

import Content from './Content'
import Footer from './Footer'
import { useCreateAutomation } from './hooks'

interface Props {
  onClose: () => void
  onSuccess: (automationId: number) => void
}

const NewAutomationModal = ({ onClose, onSuccess }: Props) => {
  const { company } = useCurrentSession()
  const [creatingAutomation, createAutomation] = useCreateAutomation()
  const [selected, setSelected] = React.useState<Trigger>()
  const [content, setContent] = React.useState(Trigger.ContentKinds.main)
  const [selectedWeekday, setSelectedWeekday] = React.useState<number>()

  const form = React.useRef(
    new Form(
      {
        name: i18n.t('automations.new.default_name', {
          number: automations.length + 1,
        }),
        repeat_date: moment().format('YYYY-MM-DD'),
      },
      {
        name: 'string',
        repeat_date: 'date',
      }
    )
  ).current

  const [name, setName] = React.useState<string>(form.get('name').value)
  const field = form.get('repeat_date')

  const onSubmit = async (closePortal: ClosePortalFn) => {
    if (!selected) return

    const repeatData = () => {
      switch (selected.get('slug')) {
        case Trigger.ContentKinds.weekly:
          if (isNil(selectedWeekday)) {
            return null
          }

          return selectedWeekday + 1

        case Trigger.ContentKinds.monthly:
          return stringToDate(field.value, 'ui').date()
        case Trigger.ContentKinds.trimester:
          return `${stringToDate(field.value, 'ui').date()} ${
            stringToDate(field.value, 'ui').month() + 1
          }`
        case Trigger.ContentKinds.yearly:
          return `${stringToDate(field.value, 'ui').date()} ${
            stringToDate(field.value, 'ui').month() + 1
          }`

        default:
          return null
      }
    }

    const { success, value: automation } = await (createAutomation as any)({
      name: form.get('name').value,
      trigger: selected.get('slug'),
      company_id: company.id,
      repeat_data: repeatData(),
    })

    if (success) {
      closePortal({
        preventOnCloseCallbackAfterClose: true,
      })
      await actions.fetch()
      onSuccess(automation.id)
    }
  }

  const enabled = !!selected && !!name

  return (
    <Modal
      illustration="calendar"
      title={i18n.t('automations.new.title')}
      description={i18n.t('automations.new.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Footer
          enabled={enabled}
          selected={selected}
          setContent={setContent}
          creatingAutomation={creatingAutomation}
        />
      }
    >
      <Box gap="s24" flexDirection="column" alignItems="center">
        <Text
          label={i18n.t('automations.automation.rename.name')}
          field={form.get('name')}
          onChange={() => setName(form.get('name').value)}
        />
        <Content
          content={content}
          selected={selected}
          setSelected={setSelected}
          selectedWeekday={selectedWeekday}
          setSelectedWeekday={setSelectedWeekday}
          field={field}
        />
      </Box>
    </Modal>
  )
}

export default observer(NewAutomationModal)
